import type { ProjectManifest } from "@pix/shared";
import {
  CHARA_POSITIONS,
  CHARA_POSITION_LABEL,
  type CharaPosition,
  type SceneRow,
} from "../grid/gridTypes";

interface Props {
  row: SceneRow | null;
  manifest: ProjectManifest;
  /** メッセージ/選択肢/セルなど、各種編集モーダルを開くトリガ */
  onEditMessage?: (row: SceneRow) => void;
  onEditChoices?: (row: SceneRow) => void;
  onEditBgm?: (row: SceneRow) => void;
  onEditBg?: (row: SceneRow) => void;
  onEditSe?: (row: SceneRow) => void;
  onEditChara?: (row: SceneRow, pos: CharaPosition) => void;
  onEditJump?: (row: SceneRow) => void;
  onEditLabel?: (row: SceneRow) => void;
}

/**
 * 選択行の編集可能な要素をまとめて表示する詳細パネル。
 * 既存のグリッドセル/コンテキストメニューと同じハンドラを共有する。
 */
export function Inspector({
  row,
  manifest,
  onEditMessage,
  onEditChoices,
  onEditBgm,
  onEditBg,
  onEditSe,
  onEditChara,
  onEditJump,
  onEditLabel,
}: Props) {
  if (!row) {
    return (
      <div className="inspector inspector-empty">
        <div className="inspector-empty-note">行を選択するとここに詳細が出ます</div>
      </div>
    );
  }

  const hasJump = row.markers.some((m) => m.kind === "jump" || m.kind === "call");
  const hasLabel = row.markers.some((m) => m.kind === "label");

  return (
    <div className="inspector">
      <div className="inspector-section">
        <div className="inspector-key">#{row.index} (line {row.sourceLine})</div>
      </div>

      <InspectorRow
        label="メッセージ"
        value={row.message ? (row.message.speaker ?? "(地の文)") + ": " + row.message.plainText : null}
        onEdit={row.message && onEditMessage ? () => onEditMessage(row) : undefined}
      />

      <InspectorRow
        label="選択肢"
        value={row.choices ? row.choices.map((c) => c.text).join(" / ") : null}
        onEdit={row.choices && onEditChoices ? () => onEditChoices(row) : undefined}
      />

      <InspectorRow
        label="BGM"
        value={row.bgm?.file ?? null}
        carry={!row.changed.bgm && row.bgm !== null}
        onEdit={onEditBgm ? () => onEditBgm(row) : undefined}
      />

      <InspectorRow
        label="背景"
        value={row.bg?.name ?? null}
        carry={!row.changed.bg && row.bg !== null}
        onEdit={onEditBg ? () => onEditBg(row) : undefined}
      />

      <InspectorRow
        label="SE"
        value={row.se?.file ?? null}
        onEdit={onEditSe ? () => onEditSe(row) : undefined}
      />

      {CHARA_POSITIONS.map((p) => {
        const c = row.chara[p];
        const def = c ? manifest.characters?.[c.id] : undefined;
        const text = c ? `${def?.name ?? c.id} (${c.expression})` : null;
        return (
          <InspectorRow
            key={p}
            label={`立ち絵 ${CHARA_POSITION_LABEL[p]}`}
            value={text}
            carry={c !== null && !row.changed.chara[p]}
            onEdit={onEditChara ? () => onEditChara(row, p) : undefined}
          />
        );
      })}

      {hasJump && (
        <InspectorRow
          label="ジャンプ"
          value={row.markers
            .filter((m) => m.kind === "jump" || m.kind === "call")
            .map((m) => {
              const t = (m as { target: string }).target;
              return `${m.kind === "call" ? "▸" : "↪"} ${t}`;
            })
            .join(" / ")}
          onEdit={onEditJump ? () => onEditJump(row) : undefined}
        />
      )}

      {hasLabel && (
        <InspectorRow
          label="ラベル"
          value={row.markers
            .filter((m) => m.kind === "label")
            .map((m) => `## ${(m as { name: string }).name}`)
            .join(" / ")}
          onEdit={onEditLabel ? () => onEditLabel(row) : undefined}
        />
      )}

      {row.variableOps.length > 0 && (
        <InspectorRow
          label="変数操作"
          value={row.variableOps.map((o) => `@${o.kind} ${o.raw}`).join("\n")}
          multiline
        />
      )}

      {row.markers.some((m) => m.kind === "wait" || m.kind === "transition" || m.kind === "effect" || m.kind === "screen") && (
        <InspectorRow
          label="演出"
          value={row.markers
            .filter((m) => m.kind === "wait" || m.kind === "transition" || m.kind === "effect" || m.kind === "screen")
            .map(stageMarkerSummary)
            .join("\n")}
          multiline
        />
      )}
    </div>
  );
}

function InspectorRow({
  label,
  value,
  onEdit,
  carry,
  multiline,
}: {
  label: string;
  value: string | null;
  onEdit?: () => void;
  carry?: boolean;
  multiline?: boolean;
}) {
  const display = value ?? "(なし)";
  return (
    <div className={"inspector-row" + (carry ? " carry" : "") + (value === null ? " empty" : "")}>
      <span className="inspector-label">{label}</span>
      <span className={"inspector-value" + (multiline ? " multiline" : "")}>{display}</span>
      {onEdit && (
        <button className="btn btn-tiny btn-secondary inspector-edit" onClick={onEdit}>
          ✎
        </button>
      )}
    </div>
  );
}

function stageMarkerSummary(m: SceneRow["markers"][number]): string {
  switch (m.kind) {
    case "wait":
      return `⏱ wait ${m.ms ?? "?"}ms`;
    case "transition":
      return `⇄ transition ${m.effect}${m.ms !== undefined ? ` ${m.ms}ms` : ""}`;
    case "effect":
      return `✨ effect ${m.name}`;
    case "screen":
      return `⌬ screen ${m.effect}`;
    default:
      return "";
  }
}
